/**
 * Shared page vocabulary for the unified page-aware assistant.
 *
 * ONE entry per page: its id, label, entity params, and the TanStack route
 * ids it covers. The web app derives its routeId→pageId mapping from
 * `routeIds` (so adding a page here wires the frontend automatically), and
 * the API's capability registry (`apps/api/src/assistant/capabilities/registry.ts`)
 * is keyed by `id`. Keeping the vocabulary here — like `user-features.ts` —
 * is what stops the two sides from drifting.
 *
 * Security note: the client-sent pageId only *selects* which capabilities the
 * agent can call; every capability handler re-validates entity ownership
 * through org-scoped repositories.
 */

export const ASSISTANT_PAGES = [
  { id: 'home', label: 'Home dashboard', entityParams: [], routeIds: ['/'] },
  { id: 'flows', label: 'Flows list', entityParams: [], routeIds: ['/flows'] },
  {
    id: 'flow-designer',
    label: 'Flow designer',
    // `runId` arrives via the ?runId search param, not the route path — the
    // web resolver carries it explicitly.
    entityParams: ['workflowId', 'runId'],
    routeIds: ['/flows/$workflowId'],
  },
  { id: 'runs', label: 'Runs list', entityParams: [], routeIds: ['/runs'] },
  { id: 'run-detail', label: 'Run detail', entityParams: ['runId'], routeIds: ['/runs/$runId'] },
  {
    id: 'releases',
    label: 'Release timeline',
    // `runId` is the selected run: the `/runs/$runId` route's path param, or
    // the timeline's `?runId=` search param (or the client's kickoff overlay
    // for the default-selected run) — `resolvePageContext` reads both.
    entityParams: ['propertyId', 'runId'],
    routeIds: ['/releases/$propertyId', '/releases/$propertyId/runs/$runId'],
  },
  { id: 'issues', label: 'Issues', entityParams: [], routeIds: ['/issues'] },
  { id: 'suites', label: 'Test suites', entityParams: [], routeIds: ['/suites'] },
  {
    id: 'suite-detail',
    label: 'Suite detail',
    entityParams: ['suiteId'],
    routeIds: ['/suites/$suiteId'],
  },
  { id: 'applications', label: 'Applications', entityParams: [], routeIds: ['/applications'] },
  {
    id: 'application-detail',
    label: 'Application detail',
    entityParams: ['propertyId'],
    routeIds: ['/applications/$propertyId'],
  },
  { id: 'exploratory', label: 'Exploratory tests', entityParams: [], routeIds: ['/exploratory'] },
  { id: 'explore', label: 'Explore', entityParams: [], routeIds: ['/explore'] },
  {
    id: 'explore-cycle',
    label: 'Exploration cycle',
    entityParams: ['cycleId'],
    routeIds: ['/explore/cycles/$cycleId'],
  },
  { id: 'settings', label: 'Settings', entityParams: [], routeIds: ['/settings'] },
  { id: 'test-data', label: 'Test data', entityParams: [], routeIds: ['/test-data'] },
  { id: 'boards', label: 'Boards', entityParams: [], routeIds: ['/boards'] },
  {
    id: 'product-map',
    label: 'Product map',
    entityParams: ['propertyId'],
    routeIds: ['/product-map'],
  },
  /** Fallback for routes without a registry entry — global capabilities only. */
  { id: 'unknown', label: 'Unknown page', entityParams: [], routeIds: [] },
] as const;

export type AssistantPageId = (typeof ASSISTANT_PAGES)[number]['id'];

export const ASSISTANT_PAGE_IDS = ASSISTANT_PAGES.map((page) => page.id) as AssistantPageId[];

export function isAssistantPageId(value: string): value is AssistantPageId {
  return (ASSISTANT_PAGE_IDS as string[]).includes(value);
}

export type AssistantPageInfo = (typeof ASSISTANT_PAGES)[number];

export function assistantPageLabel(pageId: AssistantPageId): string {
  return ASSISTANT_PAGES.find((page) => page.id === pageId)?.label ?? pageId;
}

/**
 * The name the capability meta-tool is registered under, and the only place it
 * is spelled.
 *
 * It crosses a package boundary that types cannot: the API registers the tool
 * under this key, and the web app filters AI SDK stream parts by
 * `tool-${name}`. Renaming this tool once already broke that filter silently —
 * the API side moved, no web file conflicted, and the effect that refetches
 * after an assistant write became dead code that no test could see. Import
 * this on both sides so the next rename is a compile error rather than a
 * missing refresh.
 */
export const CAPABILITY_TOOL_NAME = 'capabilities' as const;

/** The AI SDK stream part `type` for a capability tool invocation. */
export const CAPABILITY_TOOL_STREAM_PART_TYPE = `tool-${CAPABILITY_TOOL_NAME}` as const;

/**
 * Where the assistant is running. The same capability registry serves both, so
 * each capability declares which surfaces it belongs to rather than each
 * surface maintaining its own list of what it can do — that divergence is how
 * the chat agent ended up with a hand-rolled toolset that could not look
 * anything up.
 */
export const CAPABILITY_SURFACES = ['app', 'chat'] as const;

/**
 * Named `CapabilitySurface`, not `AssistantSurface`: the latter already means
 * something different in `apps/api/src/assistant/schema.ts` (which parts of a
 * page the agent may drive). Two things called the same name in one subsystem
 * is how a reader ends up confidently wrong.
 */
export type CapabilitySurface = (typeof CAPABILITY_SURFACES)[number];

/** Anything the page-scoped capability registry can be keyed by. */
export type CapabilityContextId = AssistantPageId;

/**
 * Entity scope for a capability call. Chat starts at `unknown` and supplies
 * any capability-specific entity params explicitly on the call.
 */
export type CapabilityScope = {
  pageId: CapabilityContextId;
  /**
   * Only the app surface has one, and only its prompt builder reads it — no
   * capability does. Optional so the chat surface isn't forced to invent a
   * value it has no meaning for.
   */
  pathname?: string;
  params: Record<string, string>;
};

/**
 * The page context the web client sends with every assistant chat request.
 * `params` carries the entity params named in `ASSISTANT_PAGES` for the page
 * (plus nothing else); values are prefixed or raw ids as they appear in the
 * URL — the server extracts UUIDs before touching repositories.
 */
export type AssistantPageContext = {
  pageId: AssistantPageId;
  pathname: string;
  params: Record<string, string>;
};
